import {
  BidLineItemType,
  IAccountDetailsResponse,
  IApiResponse,
  ISubscriptionResponse,
} from "@miplatform/types";

export interface ConfirmModalConfig {
  title?: string;
  description: string;
  textPositive?: string;
  textSecondaryPositive?: string;
  buttonPositiveColor?: string;
  handleSecondaryPositive?: () => Promise<IApiResponse | void>;
  hideNegative?: boolean;
}

export interface CustomerPricingDetailsContextType {
  accountDetails: IAccountDetailsResponse | null;
  isViewModelLoading: boolean;
  tabFormState: TabFormState;
  tenantDetails: ISubscriptionResponse | null;
  setTabFormState: (state: TabFormState) => void;
  setSaveHandler: (
    handler: (() => Promise<IApiResponse | void>) | null
  ) => void;
  setConfirmModalConfig: (config: ConfirmModalConfig | null) => void;
  getAccountDetails: (accountId: string) => void;
  handleSave: () => Promise<void>;
  setCancelHandler: (handler: (() => void) | null) => void;
  handleCancel: () => void;
}

export interface IAssignPriceLevelPayload {
  accountId: string;
  priceLevelId: string;
}

export interface IMaxFloorPayload {
  accountId: string;
  maxFloorId: string;
  modifiedOn?: string;
}

export interface ICategoryFloorDropTypesPayload {
  categoryId: string;
  dropTypeId: string;
  dropTypeName: string;
  floorId: string;
}

export interface IFloorsDropsFormData {
  maxFloor: IMaxFloorPayload;
  categoryFloorDropTypes: ICategoryFloorDropTypesPayload[];
}

export interface TabFormState {
  isDirty: boolean;
  isValid: boolean;
  isLoading: boolean;
}

export interface IAccountDraw {
  id?: string;
  accountId?: string;
  createdOn?: string;
  modifiedOn?: string;
  name: string;
  displayOrder: number;
  percentage: number;
}

export interface IAccountDrawsData {
  draws: IAccountDraw[];
  drawTotal: number;
}

// drop draw is the "instance" of an AccountDraw that represents the distribution for ALL drops within an account
export interface IDropDraw {
  accountId?: string;
  accountDrawId: string;
  percentage: number;
}

// category draw is an "instance" of an AccountDraw associated with a specific category
// a CategoryPricingDetails object will contain a list of these according to each AccountDraw configured for the account
export interface ICategoryDraw {
  accountDrawId: string;
  categoryId: string;
  displayOrder: number;
  name: string;
  percentage: number;
}

// this is the (dated) data structure for capturing category draw distributions; each category row will be represented with one of these.
// an array of these will be sent via API when saving
export interface ICategoryPricingDetails {
  accountId: string;
  categoryId: string;
  categoryName: string;
  drawTotal: number;
  multiplier?: number | null;
  markup?: number | null;
  modifiedOn?: string;
  draws: ICategoryDraw[];
}

// this is the form data; dropDistribution and categoryDistributions will be saved separately
export interface IDrawsDistributionFormData {
  dropDistribution: {
    draws: IDropDraw[];
    dropTotal: number;
  };
  categoryDistributions: ICategoryPricingDetails[];
}

export interface IBidLineItemPayload {
  id?: string;
  modifiedOn?: string;
  accountId: string;
  name: string;
  footnote?: string;
  categoryIds: string[];
  bidLineItemType: BidLineItemType;
}

export interface ICustomerItemsAssignPayload {
  [key: string]: string[];
}

export enum CustomerPricingDetailsTabs {
  PriceLevel = "PriceLevel",
  FloorsDrops = "FloorsDrops",
  DrawsSetup = "DrawsSetup",
  DrawsDistribution = "DrawsDistribution",
  BidLineItems = "BidLineItems",
  OptionLineItems = "OptionLineItems",
  ItemsPricing = "ItemsPricing",
  Items = "Items",
  CategoryMarkup = "CategoryMarkup",
  PriceMatrix = "PriceMatrix",
  History = "History",
}
